/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * CoverageView — Per-precinct canvassing coverage rollup (Milestone 2).
 *
 * Filter/sort spec: memory-bank/plans/filter-sort-ui-plan.md §5.2
 * API contract: /optimizer/coverage with envelope=v2
 *
 * Layout:
 *   [H1: Coverage] [Plan routes for the worst N — coordinator+ only] [Refresh]
 *   [FilterBar: date range, precinct, subregion, ld, under-contacted toggle]
 *   [Active filter chips + Clear all]
 *   [DataTable: Precinct | Voters | Contacted | Contact % | Expected ballots | Under-contacted?]
 *   [ResultsCount + pagination]
 *
 * URL-synced, server-side paginated (50 per page).
 * Default sort: contact_rate ASC (worst-covered precincts first).
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle2, Route as RouteIcon } from 'lucide-react';
import type { PrecinctCoverageRow } from '@/api/optimizer';
import {
  fetchWorstCoveredPrecincts,
  formatOptimizerError,
} from '@/api/optimizer';
import { ROUTE_WRITE_ROLES } from '@/api/routes';
import { roleMatches } from '@/lib/roles';
import { InfoTip } from '@/components/InfoTip';
import { useAuth } from '@/context/AuthContext';
import AutoplanRoutesModal from '@/components/routes/AutoplanRoutesModal';
import { useListQuery } from '@/hooks/useListQuery';
import DataTable from '@/components/DataTable';
import EmptyState from '@/components/EmptyState';
import {
  FilterBar,
  FilterDateRange,
  FilterSelect,
  FilterToggle,
} from '@/components/filters';
import client from '@/api/client';
import { ViewHeader } from '@/components/ViewHeader';
import {
  OnboardingModal,
  OnboardingHelpButton,
  hasSeenOnboarding,
} from '@/components/OnboardingModal';
import { CoachMarks } from '@/components/onboarding/CoachMarks';
import { COVERAGE_TOUR } from '@/components/onboarding/tours';
import { useOnboarding } from '@/hooks/useOnboarding';

interface PrecinctOption {
  value: string;
  label: string;
}

interface SubregionOption {
  value: string;
  label: string;
}

interface LDOption {
  value: string;
  label: string;
}

/**
 * How many worst-covered precincts "Cover the gaps" hands to the planner.
 *
 * Five is a defaulted number, not a measured one: it is a Saturday's worth of
 * turf for a small field team, and the modal it opens leaves the precinct list
 * editable, so widening is one keystroke rather than a settings trip. Naming
 * it in the button label ("worst 5") keeps the press honest — an unnumbered
 * "cover the gaps" would leave the coordinator guessing how much just got
 * planned. (memory-bank/plans/easy-buttons.md §3 #2)
 */
const WORST_N = 5;

/**
 * CoverageView — server-paginated precinct coverage list with advanced filtering.
 */
export function CoverageView({ onBack }: { onBack?: () => void }) {
  // ============================================================================
  // State for dropdowns (load from backend)
  // ============================================================================
  const [precinctOptions, setPrecinctOptions] = useState<PrecinctOption[]>([]);
  const [subregionOptions, setSubregionOptions] = useState<SubregionOption[]>([]);
  const [ldOptions, setLDOptions] = useState<LDOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // ============================================================================
  // "Cover the gaps" — Easy button #2 (memory-bank/plans/easy-buttons.md §3).
  //
  // Reading this table and then re-typing the precinct numbers into the routes
  // planner is the friction; this button carries them across. It composes two
  // permissions — reading coverage and writing routes — so its gate is the
  // INTERSECTION of the two, which is the narrower `ROUTE_WRITE_ROLES`.
  //
  // ⚠️ That intersection excludes ANALYST. An analyst belongs to
  // ANALYTICS_READ_ROLES and is meant to study this view; they are not in
  // COORDINATOR_PLUS_ROLES and `POST /routes/autoplan` will refuse them. So the
  // button is not rendered for them at all — offering an action that can only
  // 403 teaches the wrong thing about who does what. Same call the routes list
  // makes for canvassers. The endpoint stays the authority either way; this is
  // the hint (easy-buttons.md §2, "conservative in the menu").
  // ============================================================================
  const { user } = useAuth();
  const canPlanRoutes = roleMatches(user?.role, ROUTE_WRITE_ROLES);

  // Non-null precinct list = the autoplan modal is open on those precincts.
  const [gapPrecincts, setGapPrecincts] = useState<string[] | null>(null);
  const [gapCanvassers, setGapCanvassers] = useState<Array<{ id: number; full_name: string }>>([]);
  const [gapLoading, setGapLoading] = useState(false);
  const [gapError, setGapError] = useState<string | null>(null);
  const [gapNotice, setGapNotice] = useState<string | null>(null);
  // Whether the worst-N list was narrowed by the table's own filters — the
  // origin note has to say so, or "the 5 worst" reads as county-wide when it
  // is really "the 5 worst in LD 12".
  const [gapFiltered, setGapFiltered] = useState(false);

  // ============================================================================
  // Onboarding: first-visit explainer modal, then the guided coach-mark tour.
  // This view had neither before — the modal answers "what is this view", the
  // tour answers "what do I do here". Tour completion is stored per-user on the
  // server (see useOnboarding), the modal's dismissal in localStorage.
  // ============================================================================
  const [forceOnboard, setForceOnboard] = useState(false);
  const { hasSeen, markSeen, seen } = useOnboarding();
  const [tourActive, setTourActive] = useState(false);
  const onboardingDisabled =
    typeof window !== 'undefined' &&
    ((window as any).__PCGOP_DISABLE_ONBOARDING__ === true ||
      (import.meta as any).env?.VITE_DISABLE_ONBOARDING === '1');

  // Modal already dismissed on a prior visit but the tour never done — start it
  // directly. First visit instead hands off from the modal's onClose, so the two
  // never overlap. Keyed on the seen-list so it re-evaluates once /auth/me
  // resolves rather than reading an empty list on mount.
  useEffect(() => {
    if (onboardingDisabled) return;
    if (hasSeenOnboarding('coverageview') && !hasSeen(COVERAGE_TOUR.id)) {
      setTourActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen.join('|')]);

  // ============================================================================
  // Query hook: server-side list query with envelope=v2
  // ============================================================================
  const query = useListQuery<PrecinctCoverageRow>({
    endpoint: '/optimizer/coverage',
    initialParams: { envelope: 'v2', sort: 'contact_rate', order: 'asc' },
    pageSize: 50,
  });

  // ============================================================================
  // Load dropdown options on mount
  // ============================================================================
  useEffect(() => {
    async function loadOptions() {
      try {
        setLoadingOptions(true);
        const [precinctRes, ldRes, subregionRes] = await Promise.all([
          client.get('/voters/by-precinct', { params: { page_size: 500 } }),
          client.get('/voters/by-legislative-district'),
          // Canonical sub-district turfs (coarser than precinct, e.g. "12-3").
          // NOT /voters/by-subregion — that reads routes.subregion, whose live
          // values are per-precinct labels and render 1:1 with the precinct list.
          client.get('/optimizer/subregions'),
        ]);

        // Map precinct response to options (endpoint returns List[PrecinctStatsResponse])
        const precinctData = Array.isArray(precinctRes.data) ? precinctRes.data : (precinctRes.data.items || []);
        const precincts: PrecinctOption[] = precinctData.map(
          (p: any) => ({
            value: String(p.precinct_num),
            label: `P${p.precinct_num} — ${p.precinct_name || ''}`.trim(),
          })
        );
        setPrecinctOptions(precincts);

        // Map subregion response to options
        // (endpoint returns [{ subregion, name, ld, precinct_count }])
        const subregionData = Array.isArray(subregionRes.data) ? subregionRes.data : (subregionRes.data.items || []);
        const subregions: SubregionOption[] = subregionData
          .filter((s: any) => s && s.subregion)
          .map((s: any) => ({
            value: String(s.subregion),
            label: s.name ? `${s.subregion} — ${s.name}` : String(s.subregion),
          }));
        setSubregionOptions(subregions);

        // Map LD response to options (endpoint returns List[LegislativeDistrictStatsResponse])
        const ldData = Array.isArray(ldRes.data) ? ldRes.data : (ldRes.data.items || []);
        const lds: LDOption[] = ldData.map(
          (ld: any) => ({
            value: ld.district_key || ld.legislative_district,
            label: `LD ${ld.district_key || ld.legislative_district}`,
          })
        );
        setLDOptions(lds);
      } catch (err) {
        console.error('Failed to load dropdown options:', err);
        // Gracefully degrade: empty dropdowns
      } finally {
        setLoadingOptions(false);
      }
    }

    loadOptions();
  }, []);

  // ============================================================================
  // Handle filter changes
  // ============================================================================
  const handleDateRangeChange = (value: [Date | null, Date | null]) => {
    const [fromDate] = value;
    if (fromDate) {
      const dateStr = fromDate.toISOString().split('T')[0];
      query.setFilter('since', dateStr);
    } else {
      query.setFilter('since', null);
    }
  };

  const handlePrecinctChange = (value: string | null) => {
    if (value) {
      query.setFilter('precinct_num', value);
    } else {
      query.setFilter('precinct_num', null);
    }
  };

  const handleSubregionChange = (value: string | null) => {
    if (value) {
      query.setFilter('subregion', value);
    } else {
      query.setFilter('subregion', null);
    }
  };

  const handleLDChange = (value: string | null) => {
    if (value) {
      query.setFilter('legislative_district', value);
    } else {
      query.setFilter('legislative_district', null);
    }
  };

  const handleUnderContactedChange = (checked: boolean) => {
    query.setFilter('under_contacted', checked ? true : null);
  };

  // ============================================================================
  // Extract current filter values for controls and chips
  // ============================================================================
  const currentFilters = query.filters as Record<string, any>;
  const currentPrecinct = currentFilters.precinct_num
    ? String(currentFilters.precinct_num)
    : null;
  const currentSubregion = currentFilters.subregion || null;
  const currentLD = currentFilters.legislative_district || null;
  const underContactedFlag = currentFilters.under_contacted || false;

  // Parse since date for filter display
  const sinceDate = currentFilters.since
    ? new Date(currentFilters.since)
    : null;

  // ============================================================================
  // "Cover the gaps": work out WHICH precincts, then hand them to autoplan.
  //
  // The precinct list is re-fetched worst-first rather than scraped off the
  // rows on screen — see fetchWorstCoveredPrecincts for why. Whatever the user
  // has filtered to travels with it, because an Easy button must not widen the
  // reach its operator already chose (easy-buttons.md §2).
  // ============================================================================
  const handlePlanGaps = async () => {
    setGapLoading(true);
    setGapError(null);
    setGapNotice(null);
    const filters = {
      since: currentFilters.since ?? null,
      precinct_num: currentPrecinct,
      subregion: currentSubregion,
      legislative_district: currentLD,
      under_contacted: underContactedFlag ? true : null,
    };
    try {
      // The canvasser list rides along so the modal's optional "schedule these
      // as shifts" section is real here rather than claiming the campaign has
      // no canvassers. Best-effort: it is a convenience, and it is gated to the
      // same ROUTE_WRITE_ROLES as the button itself, so a failure costs the
      // shift picker and nothing else. Fetched on press, not on mount — a
      // read-only visitor never triggers it.
      const [rows, canvassers] = await Promise.all([
        fetchWorstCoveredPrecincts(WORST_N, filters),
        client
          .get('/routes/canvassers')
          .then((res: any) =>
            (res.data || []).map((u: any) => ({ id: Number(u.id), full_name: u.full_name })),
          )
          .catch(() => []),
      ]);
      const nums = rows
        .map((r: PrecinctCoverageRow) => String(r.precinct_num))
        .filter((p: string) => p && p !== 'null');
      if (nums.length === 0) {
        setGapError(
          'No precincts to plan against — nothing matches the current filters. Clear a filter and try again.',
        );
        return;
      }
      setGapCanvassers(canvassers);
      setGapFiltered(
        Boolean(currentPrecinct || currentSubregion || currentLD || underContactedFlag),
      );
      setGapPrecincts(nums);
    } catch (err) {
      setGapError(formatOptimizerError(err));
    } finally {
      setGapLoading(false);
    }
  };

  // ============================================================================
  // Build active filter chips
  // ============================================================================
  const activeFilterChips: Array<{ id: string; label: string; value: string }> = [];

  if (sinceDate) {
    activeFilterChips.push({
      id: 'since',
      label: 'Since',
      value: sinceDate.toLocaleDateString(),
    });
  }

  if (currentPrecinct) {
    const precinctLabel =
      precinctOptions.find((p) => p.value === currentPrecinct)?.label || `P${currentPrecinct}`;
    activeFilterChips.push({
      id: 'precinct',
      label: 'Precinct',
      value: precinctLabel,
    });
  }

  if (currentSubregion) {
    activeFilterChips.push({
      id: 'subregion',
      label: 'Subregion',
      value: currentSubregion,
    });
  }

  if (currentLD) {
    activeFilterChips.push({
      id: 'ld',
      label: 'LD',
      value: currentLD,
    });
  }

  if (underContactedFlag) {
    activeFilterChips.push({
      id: 'under_contacted',
      label: 'Under-contacted',
      value: 'Yes',
    });
  }

  // ============================================================================
  // Table columns definition
  // ============================================================================
  const columns = [
    {
      key: 'precinct_num',
      header: 'Precinct',
      sortable: true,
      render: (row: PrecinctCoverageRow) => (
        <span className="font-medium text-fg-primary">P{row.precinct_num}</span>
      ),
    },
    {
      key: 'voter_count',
      header: 'Voters',
      sortable: true,
      render: (row: PrecinctCoverageRow) => (
        <span className="text-right tabular-nums">{row.voter_count.toLocaleString()}</span>
      ),
    },
    {
      key: 'contacted_count',
      header: 'Contacted',
      sortable: true,
      render: (row: PrecinctCoverageRow) => (
        <span className="text-right tabular-nums">{row.contacted_count.toLocaleString()}</span>
      ),
    },
    {
      key: 'contact_rate',
      header: 'Contact %',
      sortable: true,
      render: (row: PrecinctCoverageRow) => {
        const pct = (row.contact_rate * 100).toFixed(1);
        const bgClass =
          row.contact_rate >= 0.75
            ? 'bg-status-success-bg/40 text-status-success'
            : row.contact_rate >= 0.5
              ? 'bg-status-warning-bg/40 text-status-warning'
              : 'bg-status-danger-bg/40 text-status-danger';
        return (
          <div className={`inline-block px-2 py-1 rounded text-sm font-semibold tabular-nums ${bgClass}`}>
            {pct}%
          </div>
        );
      },
    },
    {
      key: 'expected_ballots',
      header: 'Expected Ballots',
      sortable: true,
      render: (row: PrecinctCoverageRow) => (
        <span className="text-right text-fg-secondary tabular-nums">
          {Math.round(row.expected_ballots).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'under_contacted',
      header: 'Under-contacted?',
      sortable: false,
      render: (row: PrecinctCoverageRow) =>
        row.under_contacted ? (
          <span className="inline-block w-2 h-2 rounded-full bg-accent" title="Under-contacted" />
        ) : null,
    },
  ];

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="space-y-6">
      <OnboardingModal
        viewKey="coverageview"
        title="Coverage"
        intro="Where the campaign has actually knocked, precinct by precinct. Each row is a precinct with its contact rate against target, so you can see at a glance which turf is covered and which is falling behind — then send canvassers where the gaps are."
        termIds={[
          'precinct',
          'subregion',
          'legislative_district',
          'turf',
          'canvass_shift',
          'knock',
        ]}
        workflows={[
          'Filter to your own turf by precinct, subregion, or legislative district.',
          'Flip "Under-contacted only" to surface precincts behind their target.',
          'Sort by contact rate to rank where the next shift should go.',
          // Only for people who can act on it — an analyst reading this list
          // has no such button and should not be told to press one.
          ...(canPlanRoutes
            ? [
                `Press "Plan routes for the worst ${WORST_N}" to send the gaps straight to the routes planner.`,
              ]
            : []),
          'Refresh after shifts get filed — coverage recomputes from real knocks.',
        ]}
        forceOpen={forceOnboard}
        onClose={() => {
          const wasForced = forceOnboard;
          setForceOnboard(false);
          // First-visit dismissal (NOT a Help-button reopen) hands off to the tour.
          if (!wasForced && !onboardingDisabled && !hasSeen(COVERAGE_TOUR.id)) {
            setTourActive(true);
          }
        }}
      />

      {tourActive && (
        <CoachMarks
          tour={COVERAGE_TOUR}
          onFinish={() => {
            setTourActive(false);
            void markSeen(COVERAGE_TOUR.id);
          }}
          // Anchors never mounted — hide without marking seen so it re-offers.
          onAbort={() => setTourActive(false)}
        />
      )}

      {/* Header */}
      <ViewHeader
        title="Coverage"
        tone="success"
        actions={
          <>
            {canPlanRoutes && (
              <button
                type="button"
                onClick={handlePlanGaps}
                disabled={gapLoading}
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover disabled:opacity-50 text-accent-fg px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap min-h-11 sm:min-h-0"
                title={`Open the routes planner on the ${WORST_N} worst-covered precincts shown here`}
              >
                <RouteIcon size={16} />
                {gapLoading ? 'Finding the gaps…' : `Plan routes for the worst ${WORST_N}`}
              </button>
            )}
            {canPlanRoutes && <InfoTip termId="coverage_gap" />}
            <button
              onClick={query.refetch}
              disabled={query.loading}
              data-tour="coverage-refresh"
              className="bg-status-success-bg hover:bg-status-success-bg/80 disabled:opacity-50 text-fg-primary px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap min-h-11 sm:min-h-0"
              title="Refresh coverage data"
            >
              ↻ Refresh
            </button>
            <div data-tour="onboarding-help">
              <OnboardingHelpButton onClick={() => setForceOnboard(true)} />
            </div>
          </>
        }
      />

      {gapError && (
        <div
          role="alert"
          className="rounded-md border border-status-danger/40 bg-status-danger-bg/30 px-3 py-2 text-sm text-fg-primary"
        >
          {gapError}
        </div>
      )}

      {gapNotice && (
        <div
          role="status"
          className="rounded-md border border-status-success/40 bg-status-success-bg/30 px-3 py-2 text-sm text-fg-primary"
        >
          {gapNotice}
        </div>
      )}

      {/* Main filter bar. Wrapped so the tour has a stable anchor — FilterBar
          doesn't spread arbitrary props onto its DOM node. The wrapper stays a
          single child of the space-y-6 stack, so spacing is unchanged. */}
      <div data-tour="coverage-filters">
      <FilterBar
        activeFilters={activeFilterChips}
        onClearAll={query.clearAll}
      >
        <FilterDateRange
          value={[sinceDate, null]}
          onChange={handleDateRangeChange}
          presets={[
            { label: 'Last 7d', value: 'last7d' },
            { label: 'Last 30d', value: 'last30d' },
            { label: 'Last 90d', value: 'last90d' },
          ]}
        />

        <FilterSelect
          label="Precinct"
          value={currentPrecinct}
          options={precinctOptions}
          onChange={handlePrecinctChange}
          placeholder="All precincts"
        />

        <FilterSelect
          label="Subregion"
          value={currentSubregion}
          options={subregionOptions}
          onChange={handleSubregionChange}
          placeholder="All subregions"
        />

        <FilterSelect
          label="Legislative District"
          value={currentLD}
          options={ldOptions}
          onChange={handleLDChange}
          placeholder="All LDs"
        />

        <FilterToggle
          label="Under-contacted only"
          checked={underContactedFlag}
          onChange={handleUnderContactedChange}
        />
      </FilterBar>
      </div>

      {/* Data table (or EmptyState when truly empty with no active filters) */}
      {!query.loading &&
      query.items.length === 0 &&
      !query.search &&
      activeFilterChips.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title="No coverage data yet"
          description="Start a canvass shift to build coverage maps."
        />
      ) : (
        <DataTable<PrecinctCoverageRow>
          columns={columns}
          query={query}
          rowKey={(row) => row.precinct_num}
          emptyMessage="No precincts in this campaign."
          emptyFilteredMessage="No precincts match these filters."
          mobileMode="cards"
        />
      )}

      {/* The composition itself: the SAME planner the Routes list uses, opened
          on the precincts this view just ranked. One planner, one preview, one
          commit — no second code path that could plan differently. */}
      {gapPrecincts && (
        <AutoplanRoutesModal
          initialPrecincts={gapPrecincts}
          canvassers={gapCanvassers}
          originNote={
            <>
              Planning the{' '}
              <span className="font-semibold text-fg-primary">
                {gapPrecincts.length}
              </span>{' '}
              worst-covered {gapPrecincts.length === 1 ? 'precinct' : 'precincts'}{' '}
              {gapFiltered ? 'matching your coverage filters' : 'in your district'}:{' '}
              <span className="font-semibold text-fg-primary">
                {gapPrecincts.map((p) => `P${p}`).join(', ')}
              </span>
              . Every other precinct is left out — edit the Precincts field below
              to widen or narrow it.
            </>
          }
          onClose={() => setGapPrecincts(null)}
          onCreated={(routeCount: number, shiftCount: number) => {
            setGapPrecincts(null);
            setGapNotice(
              `Created ${routeCount} draft ${routeCount === 1 ? 'route' : 'routes'}` +
                (shiftCount > 0
                  ? ` and scheduled ${shiftCount} ${shiftCount === 1 ? 'shift' : 'shifts'}.`
                  : '. Find them under Routes, review, then set them active.'),
            );
            // Coverage counts knocks, not routes, so these numbers will not move
            // until the walks happen — refetch anyway so a shift filed in the
            // meantime is not left stale on screen.
            query.refetch?.();
          }}
        />
      )}
    </div>
  );
}

export default CoverageView;
